import { useState, useEffect, useCallback } from 'react';
import { HubApi } from '../services/HubApi';
import { DynamicOptions, SelectOption } from '../shared/components/AdvancedExpressionBuilder/types';

interface UseResourceResolutionProps {
  dynamicOptions?: DynamicOptions;
  organizationId: string;
  search?: string;
}

interface UseResourceResolutionReturn {
  options: SelectOption[];
  isLoading: boolean;
  error?: string;
}

const resourceCache = new Map<string, { data: any[]; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000;

const getCacheKey = (organizationId: string, resource: string): string => {
  return `${organizationId}:${resource}`;
};

const isCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < CACHE_DURATION;
};

export const getResourceCacheStats = (): { size: number; keys: string[] } => {
  return {
    size: resourceCache.size,
    keys: Array.from(resourceCache.keys())
  };
};

const interpolateString = (template: string, data: Record<string, any>): string => {
  return template.replace(/\{\{(\w+(?:\.\w+)*)\}\}/g, (match, key) => {
    const keys = key.split('.');
    let value = data;
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) return match;
    }
    return value?.toString() || match;
  });
};

export const useResourceResolution = ({
  dynamicOptions,
  organizationId,
  search
}: UseResourceResolutionProps): UseResourceResolutionReturn => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [allOptions, setAllOptions] = useState<SelectOption[]>([]);

  const fetchAllOptions = useCallback(async () => {
    if (!dynamicOptions) {
      setAllOptions([]);
      setOptions([]);
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      const cacheKey = getCacheKey(organizationId, dynamicOptions.resource);
      const cachedData = resourceCache.get(cacheKey);

      if (cachedData && isCacheValid(cachedData.timestamp)) {
        console.log(`📦 Using cached data for ${cacheKey}`);
        const processedOptions = cachedData.data.map((item: any) => ({
          value: interpolateString(dynamicOptions.mapValue, item),
          label: interpolateString(dynamicOptions.mapLabel, item),
          group: dynamicOptions.groupBy 
            ? interpolateString(dynamicOptions.groupBy, item) 
            : undefined
        }));
        setAllOptions(processedOptions);
        setIsLoading(false);
        return;
      }

      console.log(`🌐 Fetching fresh data for ${cacheKey}`);
      const response = await HubApi.Authorization.getResources({
        organizationId: organizationId,
        resource: dynamicOptions.resource
      });

      resourceCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      });

      const processedOptions = response.map((item: any) => ({
        value: interpolateString(dynamicOptions.mapValue, item),
        label: interpolateString(dynamicOptions.mapLabel, item),
        group: dynamicOptions.groupBy 
          ? interpolateString(dynamicOptions.groupBy, item) 
          : undefined
      }));

      setAllOptions(processedOptions);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setError('Failed to fetch options');
      setAllOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [dynamicOptions, organizationId]);

  useEffect(() => {
    if (!search) {
      setOptions(allOptions);
    } else {
      const filteredOptions = allOptions.filter(option => 
        option.label.toLowerCase().includes(search.toLowerCase())
      );
      setOptions(filteredOptions);
    }
  }, [allOptions, search]);

  useEffect(() => {
    fetchAllOptions();
  }, [fetchAllOptions]);

  return { options, isLoading, error };
}; 